/**
 * M05 Prompt 管理 shell · 版本、d' 模板、灰度、沙盒。
 */
import {
	ArrowRight,
	CheckCircle2,
	ClipboardCheck,
	Play,
	RotateCw,
	Save,
	Settings2,
	ShieldCheck,
	XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { type RuleSurfaceRow, RuleSurfaceSummary } from "@/components/shared/rule-surface-summary";
import { SingleStageRuntimePanel } from "@/components/shared/single-stage-runtime-panel";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
	type PromptSandboxRequest,
	type PromptVersionListItem,
	useDPrimeTemplate,
	useMainAgentTemplate,
	usePatchDPrimeTemplate,
	usePatchMainAgentTemplate,
	usePromptABConfig,
	usePromptSandbox,
	usePromptVersions,
	usePutPromptABConfig,
} from "@/lib/api/prompts";
import {
	type AgentQualityMode,
	type AgentRuntimeConfig,
	type AgentWriteMode,
	type WriteActionItem,
	useAgentRuntimeConfig,
	useConfirmWriteAction,
	usePutAgentRuntimeConfig,
	useRejectWriteAction,
	useWriteActions,
} from "@/lib/api/runtime";

const DEFAULT_MESSAGE = "帮我查一下订单 #1001 的物流状态";
const DEFAULT_MERCHANT_ID = "mer_devcons";

const DEFAULT_D_PRIME_FIXTURE: PromptSandboxRequest["d_prime_fixture"] = {
	auto_invoked_results: [
		{
			skill_id: "SK01",
			status: "failed",
			summary: "Shopify PCD blocked order lookup",
		},
	],
	rag_snippets: [
		{
			namespace: "policy",
			doc_id: "shipping-policy",
			content: "物流查询需订单读取权限。",
			score: 0.91,
		},
	],
};

const PROMPT_ASSET_TEST_ROWS = [
	{
		asset: "主回复提示词",
		effect: "真实生效",
		scope: "生成最终回复时生效；当运行记录显示已被模型消费时，才代表真实影响对客回复。",
		evidence: "运行记录里的提示词版本快照和提示词消费记录",
		testPath: "沙盒预览先看变量和证据区，链路测试台看真实消费，评测运行看批量效果。",
		configHref: "/prompt-q#prompt-management",
		testHref: "/prompt-q#prompt-sandbox",
	},
	{
		asset: "d' 证据区模板",
		effect: "真实生效",
		scope: "把工具结果和知识片段组织进回复前证据区，影响事实表达。",
		evidence: "回复生成摘要里的证据引用和注入变量",
		testPath: "沙盒预览证据区结构；端到端运行确认事实是否进入最终回复。",
		configHref: "/prompt-q#prompt-management",
		testHref: "/prompt-q#prompt-sandbox",
	},
	{
		asset: "分类提示词灰度",
		effect: "配置已落，运行链路待接入",
		scope: "商户配置可读写，但当前主链路未确认按商户消费该提示词。",
		evidence: "商户配置中的分类提示词版本；现状审计标记为待接入",
		testPath: "先用分类管理核验配置，真实分类效果以 L2 规则和端到端结果为准。",
		configHref: "/intent",
		testHref: "/intent#intent-test",
	},
	{
		asset: "方案引导与兜底",
		effect: "真实生效",
		scope: "方案匹配后进入回复上下文，影响处理步骤、工具计划和兜底回复。",
		evidence: "运行记录里的命中方案、匹配方式和方案版本",
		testPath: "方案管理看配置，单环节测试看方案命中，评测运行看是否新增方案失败。",
		configHref: "/playbook",
		testHref: "/playbook#playbook-test",
	},
	{
		asset: "工具参数和返回文案",
		effect: "条件生效",
		scope: "按方案允许工具和运行权限进入预取或动作验证；写操作仍有保护性试跑边界。",
		evidence: "运行记录里的建议工具、通用工具和 Shopify 接入状态",
		testPath: "工具与动作页做 sandbox，链路测试台看工具结果是否进入证据和回复。",
		configHref: "/skill-tool#skill-catalog",
		testHref: "/skill-tool#skill-action-verify",
	},
	{
		asset: "知识检索内容",
		effect: "沙盒和主链路分离",
		scope: "知识文档可维护；本地检索预览不等同主链路 Qdrant/RAG 命中。",
		evidence: "知识沙盒审计号和召回阶段的知识片段数量",
		testPath: "检索预览看可找回片段，端到端运行确认是否进入回复证据。",
		configHref: "/knowledge#knowledge-search-verify",
		testHref: "/knowledge#knowledge-search-verify",
	},
	{
		asset: "质量策略",
		effect: "待开发",
		scope: "后续用于阻断、改写、转人工和放行；当前不要包装成完整质量门禁。",
		evidence: "P8/P9-GOV 质量策略待开发位",
		testPath: "先在菜单保留策略配置/策略测试，待最小规则落地后接评测运行。",
		configHref: "/quality",
		testHref: "/quality",
	},
	{
		asset: "评测判断规则",
		effect: "待开发",
		scope: "影响评测结论，不直接影响对客回复；需要产品可读规则和场景指标绑定。",
		evidence: "场景指标和评价规则规划",
		testPath: "先做场景指标和评价口径，再进入完整判断规则配置。",
		configHref: "/eval#eval-run",
		testHref: "/eval#eval-run",
	},
] as const;

const ASSET_CHANGE_AUDIT_STEPS = [
	{
		step: "01",
		title: "先记录变更意图",
		owner: "产品 / 运营",
		detail: "说明这次是为了解决哪个 badcase、哪个场景指标或哪类回复问题。",
		evidence: "change_reason、source_badcase_id、scenario_key",
	},
	{
		step: "02",
		title: "绑定责任资产和版本",
		owner: "运营 / 研发",
		detail: "明确改的是提示词、规则、方案、工具、知识还是评价规则，并记录改前改后版本。",
		evidence: "asset_type、asset_id、before_version、after_version、responsible_asset",
	},
	{
		step: "03",
		title: "跑单环节测试",
		owner: "运营 / QA",
		detail: "先验证意图、方案、工具或知识局部能力是否符合预期，避免直接用端到端结果猜原因。",
		evidence: "single_stage_result、trace_id、field_refs",
	},
	{
		step: "04",
		title: "跑端到端和评测",
		owner: "QA / 产品",
		detail: "用链路测试台确认真实回复，再用评测运行确认一组样本是否整体变好。",
		evidence: "admin_run_id、eval_run_id、compare_snapshot_id",
	},
	{
		step: "05",
		title: "写回复盘结论",
		owner: "产品 / 运营",
		detail: "记录是否发布、回滚、继续观察，以及哪些 badcase 被关闭或转入下一轮。",
		evidence: "decision、rollback_condition、closed_badcase_ids、next_action",
	},
] as const;

const ASSET_CHANGE_AUDIT_FIELDS = [
	"asset_type",
	"asset_id",
	"before_version",
	"after_version",
	"change_reason",
	"source_badcase_id",
	"single_stage_result",
	"eval_run_id",
] as const;

const MAIN_AGENT_TOOL_MODE_ROWS = [
	{
		mode: "默认",
		title: "预取式证据注入",
		status: "已落主路径",
		detail: "先按方案和语义准备工具/知识证据，注入后再由主 Agent 生成回复。",
		check: "看 evidence_refs、not_triggered、prompt_snapshot",
	},
	{
		mode: "只读",
		title: "自主只读工具",
		status: "M0 已接入",
		detail:
			"SDK loop 已有只读工具注册、白名单、预算、去重和 trace 字段；默认仍可用开关回退旧 runner。",
		check: "看 loop_meta、executed_actions、缓存命中和生成模式",
	},
	{
		mode: "计划",
		title: "写操作计划",
		status: "M1 已接入",
		detail:
			"取消、退货、退款、改地址在 SDK loop 内只走 plan-only dry-run，并形成 confirmation_plan。",
		check: "看 confirmation_plan、write_simulated、mutation_executed=false",
	},
	{
		mode: "试跑",
		title: "保护性试跑与质量门",
		status: "Q-deep 已接入",
		detail: "Phase5 和确认队列都会检查 dry-run、blocked_operations、幂等键和越权 live 声明。",
		check: "看 side_effect_check、q_gate、blocked_operations",
	},
	{
		mode: "写入",
		title: "真实写入",
		status: "已上线受控",
		detail:
			"真实修改 Shopify 只能从 Admin 待确认队列进入，必须通过确认、Q-deep、持久审计和幂等键。",
		check: "看 write_action_audits、q_gate、live_result",
	},
] as const;

const MAIN_AGENT_TOOL_MODE_FACTS = [
	"当前默认主链路已启用 SDK read loop；只读补查仍受方案白名单、步数预算和去重保护。",
	"主 Agent 只能把 SK07-SK10 推进到 plan-only dry-run，并生成 confirmation_plan。",
	"真实写入不允许由主 Agent 直接执行，只能从 Admin 写动作确认队列确认后进入 live write。",
	"live write 依赖 q_deep_side_effect、idempotency_key 和持久 write_action_audits 记录。",
] as const;

const PROMPT_SINGLE_STAGE_FACT_ROWS = [
	{
		label: "输入事实",
		value: "回复生成",
		detail: "从链路测试台或样本资产进入时，只确认回复生成合同，不把它说成完整自主工具调度。",
	},
	{
		label: "证据快照",
		value: "prompt_snapshot / evidence_refs",
		detail: "核对主 Agent 使用了哪些提示词版本、证据片段和注入变量。",
	},
	{
		label: "输出判断",
		value: "final_answer / action_type",
		detail: "看最终回复是否追问、引用事实、表达边界，并避免越权承诺。",
	},
	{
		label: "当前边界",
		value: "可直接运行",
		detail: "复用运行生成事件做局部验证；不新增独立提示词接口，不改主回复链路。",
	},
] as const;

const PROMPT_SINGLE_STAGE_CHECK_ROWS = [
	{
		check: "证据使用",
		expectation: "回复应基于 evidence_refs 和 d' 证据区，不编造订单、库存、政策或折扣事实。",
		sample: "P10TA004 / P10TA005",
	},
	{
		check: "追问推进",
		expectation: "缺订单号、缺目标商品或缺确认信息时，优先追问并说明为什么需要补充。",
		sample: "P10TA001",
	},
	{
		check: "风险承诺",
		expectation: "取消、退款、改地址等写操作只能给计划、保护性试跑或确认要求，不承诺已执行。",
		sample: "P10TA010",
	},
	{
		check: "语气与边界",
		expectation: "语气保持客服可读，结论、下一步和限制条件要清楚，复杂场景不堆长模板。",
		sample: "全量 P10 首批样本",
	},
] as const;

const PROMPT_RULE_ROWS: RuleSurfaceRow[] = [
	{
		name: "主 Agent 提示词",
		source: "PromptVersion.main_agent_template",
		edit: "可保存当前版本正文",
		effect: "主链路生效",
		verify: "链路测试台看 prompt_version 和 consumed_by_llm",
		tone: "live",
	},
	{
		name: "证据区模板",
		source: "PromptVersion.d_prime_template",
		edit: "可保存当前版本正文",
		effect: "主链路生效",
		verify: "看 prompt_snapshot、evidence_refs 和最终回复",
		tone: "live",
	},
	{
		name: "商户灰度",
		source: "MerchantConfig.system_prompt_version_a/b/ratio",
		edit: "可按商户保存版本和比例",
		effect: "按商户生效",
		verify: "确认商户 ID 后，用同一商户跑链路测试",
		tone: "live",
	},
	{
		name: "沙盒预览",
		source: "模拟问题和模拟证据",
		edit: "不发布，只预览",
		effect: "仅沙盒",
		verify: "用于保存前检查变量，不代表真实客户回复",
		tone: "sandbox",
	},
];

export type M05View = "management" | "sandbox" | "runtime";

export function M05Shell({ view = "management" }: { view?: M05View }) {
	const versions = usePromptVersions();
	const mainAgent = useMainAgentTemplate("m05-v1");
	const dPrime = useDPrimeTemplate("m05-v1");
	const saveMainAgent = usePatchMainAgentTemplate();
	const saveDPrime = usePatchDPrimeTemplate();
	const [mainTemplate, setMainTemplate] = useState("");
	const [mainStatus, setMainStatus] = useState<string | null>(null);
	const [template, setTemplate] = useState("");
	const [dPrimeStatus, setDPrimeStatus] = useState<string | null>(null);

	const [merchantId, setMerchantId] = useState(DEFAULT_MERCHANT_ID);
	const [versionA, setVersionA] = useState("m05-v1");
	const [versionB, setVersionB] = useState("");
	const [ratio, setRatio] = useState(0);
	const [abStatus, setAbStatus] = useState<string | null>(null);
	const abConfig = usePromptABConfig(merchantId);
	const saveAB = usePutPromptABConfig(merchantId);
	const runtimeConfig = useAgentRuntimeConfig(merchantId);
	const saveRuntimeConfig = usePutAgentRuntimeConfig(merchantId);
	const writeActions = useWriteActions(merchantId, "pending_confirmation");
	const confirmWriteAction = useConfirmWriteAction(merchantId);
	const rejectWriteAction = useRejectWriteAction(merchantId);
	const [sdkLoopEnabled, setSdkLoopEnabled] = useState(true);
	const [maxSteps, setMaxSteps] = useState(6);
	const [qualityMode, setQualityMode] = useState<AgentQualityMode>("q_deep_side_effect");
	const [writeMode, setWriteMode] = useState<AgentWriteMode>("live_confirmed");
	const [runtimeNotes, setRuntimeNotes] = useState("");
	const [runtimeStatus, setRuntimeStatus] = useState<string | null>(null);
	const [writeActionStatus, setWriteActionStatus] = useState<string | null>(null);

	const [sandboxMessage, setSandboxMessage] = useState(DEFAULT_MESSAGE);
	const sandbox = usePromptSandbox();

	useEffect(() => {
		if (dPrime.data?.template) setTemplate(dPrime.data.template);
	}, [dPrime.data?.template]);

	useEffect(() => {
		if (mainAgent.data?.template) setMainTemplate(mainAgent.data.template);
	}, [mainAgent.data?.template]);

	useEffect(() => {
		if (!abConfig.data) return;
		setVersionA(abConfig.data.system_prompt_version_a);
		setVersionB(abConfig.data.system_prompt_version_b ?? "");
		setRatio(abConfig.data.system_prompt_ab_ratio);
	}, [abConfig.data]);

	useEffect(() => {
		if (!runtimeConfig.data) return;
		setSdkLoopEnabled(runtimeConfig.data.sdk_loop_enabled);
		setMaxSteps(runtimeConfig.data.max_steps);
		setQualityMode(runtimeConfig.data.quality_mode);
		setWriteMode(runtimeConfig.data.write_mode);
		setRuntimeNotes(runtimeConfig.data.notes ?? "");
	}, [runtimeConfig.data]);

	const activeVersion = useMemo(
		() => versions.data?.items.find((item) => item.status === "published") ?? null,
		[versions.data?.items],
	);

	function handleSaveDPrime() {
		setDPrimeStatus(null);
		saveDPrime.mutate(
			{
				version_id: "m05-v1",
				template,
				updated_by: "admin-console",
				notes: "M05 Day12 d-prime template update",
			},
			{
				onSuccess: (data) => {
					setDPrimeStatus(`已保存 · ${data.token_estimate} tokens`);
				},
				onError: (error) => {
					setDPrimeStatus(error instanceof Error ? error.message : "保存失败");
				},
			},
		);
	}

	function handleSaveMainAgent() {
		setMainStatus(null);
		saveMainAgent.mutate(
			{
				version_id: "m05-v1",
				template: mainTemplate,
				updated_by: "admin-console",
				notes: "M05 main agent baseline prompt update",
			},
			{
				onSuccess: (data) => {
					setMainStatus(`已保存 · ${data.token_estimate} tokens`);
				},
				onError: (error) => {
					setMainStatus(error instanceof Error ? error.message : "保存失败");
				},
			},
		);
	}

	function handleSaveAB() {
		setAbStatus(null);
		saveAB.mutate(
			{
				system_prompt_version_a: versionA,
				system_prompt_version_b: versionB.trim() ? versionB.trim() : null,
				system_prompt_ab_ratio: ratio,
			},
			{
				onSuccess: (data) => {
					setAbStatus(`已保存 · ${data.system_prompt_ab_ratio}%`);
				},
				onError: (error) => {
					setAbStatus(error instanceof Error ? error.message : "保存失败");
				},
			},
		);
	}

	function handleRunSandbox() {
		sandbox.mutate({
			merchant_id: merchantId,
			user_message: sandboxMessage,
			prompt_version: versionA,
			allowed_skills: ["SK01", "SK02", "SK05", "SK11"],
			d_prime_fixture: DEFAULT_D_PRIME_FIXTURE,
		});
	}

	function handleSaveRuntimeConfig() {
		setRuntimeStatus(null);
		saveRuntimeConfig.mutate(
			{
				sdk_loop_enabled: sdkLoopEnabled,
				max_steps: maxSteps,
				quality_mode: qualityMode,
				write_mode: writeMode,
				updated_by: "admin-console",
				notes: runtimeNotes.trim() ? runtimeNotes.trim() : null,
			},
			{
				onSuccess: (data) => {
					setRuntimeStatus(`已保存 · ${runtimeWriteModeLabel(data.write_mode)}`);
				},
				onError: (error) => {
					setRuntimeStatus(error instanceof Error ? error.message : "保存失败");
				},
			},
		);
	}

	function handleConfirmWriteAction(item: WriteActionItem) {
		setWriteActionStatus(null);
		confirmWriteAction.mutate(
			{ auditId: item.audit_id, idempotencyKey: item.idempotency_key },
			{
				onSuccess: () => setWriteActionStatus(`已确认执行 · ${item.audit_id}`),
				onError: (error) =>
					setWriteActionStatus(error instanceof Error ? error.message : "确认执行失败"),
			},
		);
	}

	function handleRejectWriteAction(item: WriteActionItem) {
		setWriteActionStatus(null);
		rejectWriteAction.mutate(
			{ auditId: item.audit_id },
			{
				onSuccess: () => setWriteActionStatus(`已拒绝 · ${item.audit_id}`),
				onError: (error) =>
					setWriteActionStatus(error instanceof Error ? error.message : "拒绝失败"),
			},
		);
	}

	if (versions.isLoading || mainAgent.isLoading || dPrime.isLoading) {
		return (
			<div data-testid="m05-loading" className="p-4 text-sm text-text-secondary">
				加载提示词配置中...
			</div>
		);
	}

	if (versions.isError || mainAgent.isError || dPrime.isError) {
		const err = versions.error ?? mainAgent.error ?? dPrime.error;
		return (
			<div data-testid="m05-error" className="p-4 text-sm text-brand-danger">
				加载失败：{err instanceof Error ? err.message : String(err)}
			</div>
		);
	}

	return (
		<div className="space-y-4" data-testid="m05-shell" data-view={view}>
			<RuleSurfaceSummary
				rows={PROMPT_RULE_ROWS}
				testId="prompt-rule-surface-summary"
				description="提示词保存后是否生效，取决于当前测试商户使用哪个 Prompt 版本；沙盒只看预览，端到端才看真实消费。"
			/>

			{view === "management" ? (
				<>
					<div className="grid gap-3 md:grid-cols-4">
						<Metric label="提示词版本" value={versions.data?.total ?? 0} />
						<Metric label="当前版本" value={activeVersion?.version_id ?? "m05-v1"} compact />
						<Metric label="主提示词 tokens" value={mainAgent.data?.token_estimate ?? 0} />
						<Metric label="证据区 tokens" value={dPrime.data?.token_estimate ?? 0} />
					</div>

					<section className="overflow-x-auto rounded-md border border-surface-border">
						<table className="w-full border-collapse text-sm" data-testid="m05-version-table">
							<thead className="bg-surface-subtle">
								<tr className="text-left">
									<th className="px-3 py-2 font-medium">版本</th>
									<th className="px-3 py-2 font-medium">状态</th>
									<th className="px-3 py-2 font-medium">证据区数量</th>
									<th className="px-3 py-2 font-medium">主提示词 tokens</th>
									<th className="px-3 py-2 font-medium">证据区 tokens</th>
									<th className="px-3 py-2 font-medium">更新人</th>
								</tr>
							</thead>
							<tbody>
								{(versions.data?.items ?? []).map((item) => (
									<VersionRow key={item.version_id} item={item} />
								))}
							</tbody>
						</table>
					</section>

					<section className="grid gap-4 rounded-md border border-surface-border p-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.9fr)]">
						<div className="space-y-3">
							<div className="flex items-center justify-between gap-3">
								<div>
									<h3 className="text-base font-semibold">主 Agent 基准提示词</h3>
									<p className="mt-1 text-sm text-text-secondary">
										定义角色、处理原则、上下文注入位和输出合约。
									</p>
								</div>
								<Button
									type="button"
									variant="outline"
									onClick={() => setMainTemplate(mainAgent.data?.template ?? "")}
								>
									<RotateCw className="mr-2 h-4 w-4" />
									还原
								</Button>
							</div>
							<textarea
								value={mainTemplate}
								onChange={(event) => setMainTemplate(event.target.value)}
								className="min-h-80 w-full rounded-md border border-surface-border bg-surface-background p-3 font-mono text-xs"
								data-testid="m05-main-agent-template"
							/>
							<div className="flex items-center gap-3">
								<Button
									type="button"
									onClick={handleSaveMainAgent}
									data-testid="m05-save-main-agent"
								>
									<Save className="mr-2 h-4 w-4" />
									保存主提示词
								</Button>
								<span className="text-sm text-text-secondary" data-testid="m05-main-agent-status">
									{saveMainAgent.isPending ? "保存中..." : mainStatus}
								</span>
							</div>
						</div>

						<div className="space-y-3">
							<h3 className="text-base font-semibold">变量插槽</h3>
							<div className="grid gap-2 text-sm">
								{(mainAgent.data?.variables ?? []).map((item) => (
									<div
										key={item}
										className="rounded-md border border-surface-border bg-surface-card px-3 py-2 font-mono text-xs"
									>
										{`{{${item}}}`}
									</div>
								))}
							</div>
						</div>
					</section>

					<section className="grid gap-4 rounded-md border border-surface-border p-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.9fr)]">
						<div className="space-y-3">
							<div className="flex items-center justify-between gap-3">
								<div>
									<h3 className="text-base font-semibold">证据区模板</h3>
									<p className="mt-1 text-sm text-text-secondary">
										管理工具结果和知识片段进入回复前的组织方式。
									</p>
								</div>
								<Button
									type="button"
									variant="outline"
									onClick={() => setTemplate(dPrime.data?.template ?? "")}
								>
									<RotateCw className="mr-2 h-4 w-4" />
									还原
								</Button>
							</div>
							<textarea
								value={template}
								onChange={(event) => setTemplate(event.target.value)}
								className="min-h-64 w-full rounded-md border border-surface-border bg-surface-background p-3 font-mono text-xs"
								data-testid="m05-d-prime-template"
							/>
							<div className="flex items-center gap-3">
								<Button type="button" onClick={handleSaveDPrime} data-testid="m05-save-d-prime">
									<Save className="mr-2 h-4 w-4" />
									保存模板
								</Button>
								<span className="text-sm text-text-secondary" data-testid="m05-d-prime-status">
									{saveDPrime.isPending ? "保存中..." : dPrimeStatus}
								</span>
							</div>
						</div>

						<div className="space-y-3">
							<h3 className="text-base font-semibold">商户灰度</h3>
							<label className="grid gap-1 text-sm">
								<span className="text-xs text-text-muted">商户 ID</span>
								<input
									value={merchantId}
									onChange={(event) => setMerchantId(event.target.value)}
									className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
								/>
							</label>
							<div className="grid gap-2 sm:grid-cols-2">
								<label className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">主版本</span>
									<input
										value={versionA}
										onChange={(event) => setVersionA(event.target.value)}
										className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
									/>
								</label>
								<label className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">灰度版本</span>
									<input
										value={versionB}
										onChange={(event) => setVersionB(event.target.value)}
										className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
										data-testid="m05-version-b"
									/>
								</label>
							</div>
							<div className="flex flex-wrap gap-2">
								{[0, 5, 20, 100].map((value) => (
									<Button
										key={value}
										type="button"
										variant={ratio === value ? "default" : "outline"}
										size="sm"
										onClick={() => setRatio(value)}
										data-testid={`m05-ratio-${value}`}
									>
										{value}%
									</Button>
								))}
							</div>
							<div className="flex items-center gap-3">
								<Button type="button" onClick={handleSaveAB} data-testid="m05-save-ab">
									<Save className="mr-2 h-4 w-4" />
									保存灰度
								</Button>
								<span className="text-sm text-text-secondary" data-testid="m05-ab-status">
									{saveAB.isPending ? "保存中..." : abStatus}
								</span>
							</div>
						</div>
					</section>
				</>
			) : view === "runtime" ? (
				<RuntimeOperationsPanel
					merchantId={merchantId}
					setMerchantId={setMerchantId}
					runtimeConfig={runtimeConfig.data ?? null}
					isRuntimeLoading={runtimeConfig.isLoading}
					sdkLoopEnabled={sdkLoopEnabled}
					setSdkLoopEnabled={setSdkLoopEnabled}
					maxSteps={maxSteps}
					setMaxSteps={setMaxSteps}
					qualityMode={qualityMode}
					setQualityMode={setQualityMode}
					writeMode={writeMode}
					setWriteMode={setWriteMode}
					runtimeNotes={runtimeNotes}
					setRuntimeNotes={setRuntimeNotes}
					onSaveRuntime={handleSaveRuntimeConfig}
					runtimeStatus={saveRuntimeConfig.isPending ? "保存中..." : runtimeStatus}
					writeActions={writeActions.data?.items ?? []}
					isWriteActionsLoading={writeActions.isLoading}
					onConfirmWriteAction={handleConfirmWriteAction}
					onRejectWriteAction={handleRejectWriteAction}
					writeActionStatus={
						confirmWriteAction.isPending || rejectWriteAction.isPending
							? "处理中..."
							: writeActionStatus
					}
				/>
			) : (
				<>
					<section className="grid gap-4 rounded-md border border-surface-border p-4 lg:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.2fr)]">
						<div className="space-y-3">
							<h3 className="text-base font-semibold">沙盒预览</h3>
							<p className="text-sm leading-6 text-text-secondary">
								用模拟问题先看证据区、主提示词预览和阶段输出，再决定是否进入链路测试台或评测运行。
							</p>
							<textarea
								value={sandboxMessage}
								onChange={(event) => setSandboxMessage(event.target.value)}
								className="min-h-28 w-full rounded-md border border-surface-border bg-surface-background p-3 text-sm"
							/>
							<Button type="button" onClick={handleRunSandbox} data-testid="m05-sandbox-run">
								<Play className="mr-2 h-4 w-4" />
								运行沙盒
							</Button>
						</div>
						<div className="grid gap-3 md:grid-cols-2">
							<pre
								className="max-h-72 overflow-auto rounded-md bg-surface-subtle p-3 text-xs"
								data-testid="m05-sandbox-result"
							>
								{sandbox.isPending
									? "运行中..."
									: sandbox.data
										? JSON.stringify(sandbox.data.phase_preview, null, 2)
										: "未运行"}
							</pre>
							<pre
								className="max-h-72 overflow-auto rounded-md bg-surface-subtle p-3 text-xs"
								data-testid="m05-main-agent-preview"
							>
								{sandbox.data?.main_agent_prompt_preview ?? "main agent preview"}
							</pre>
							<pre
								className="max-h-72 overflow-auto rounded-md bg-surface-subtle p-3 text-xs md:col-span-2"
								data-testid="m05-d-prime-preview"
							>
								{sandbox.data?.d_prime_preview ?? "d' preview"}
							</pre>
						</div>
					</section>

					<SingleStageRuntimePanel
						kind="prompt"
						targetStage="prompt_response"
						sampleId="P10TA001"
						sampleMessage="我要退款，但是忘记订单号了"
						expectedIntent="refund_request"
						expectedPlaybook="PB02_refund"
						assetName="主 Agent 提示词 / 证据区模板 / 生成事件"
						boundary="当前复用回复生成事件做局部验证，不新增独立提示词执行接口。"
					/>
					<PromptAssetTestMatrix />
					<PromptSingleStageContract />
					<MainAgentToolModeExplainer />
					<PromptAssetChangeAudit />
				</>
			)}
		</div>
	);
}

function RuntimeOperationsPanel({
	merchantId,
	setMerchantId,
	runtimeConfig,
	isRuntimeLoading,
	sdkLoopEnabled,
	setSdkLoopEnabled,
	maxSteps,
	setMaxSteps,
	qualityMode,
	setQualityMode,
	writeMode,
	setWriteMode,
	runtimeNotes,
	setRuntimeNotes,
	onSaveRuntime,
	runtimeStatus,
	writeActions,
	isWriteActionsLoading,
	onConfirmWriteAction,
	onRejectWriteAction,
	writeActionStatus,
}: {
	merchantId: string;
	setMerchantId: (value: string) => void;
	runtimeConfig: AgentRuntimeConfig | null;
	isRuntimeLoading: boolean;
	sdkLoopEnabled: boolean;
	setSdkLoopEnabled: (value: boolean) => void;
	maxSteps: number;
	setMaxSteps: (value: number) => void;
	qualityMode: AgentQualityMode;
	setQualityMode: (value: AgentQualityMode) => void;
	writeMode: AgentWriteMode;
	setWriteMode: (value: AgentWriteMode) => void;
	runtimeNotes: string;
	setRuntimeNotes: (value: string) => void;
	onSaveRuntime: () => void;
	runtimeStatus: string | null;
	writeActions: WriteActionItem[];
	isWriteActionsLoading: boolean;
	onConfirmWriteAction: (item: WriteActionItem) => void;
	onRejectWriteAction: (item: WriteActionItem) => void;
	writeActionStatus: string | null;
}) {
	const liveConfirmEnabled = writeMode === "live_confirmed";
	return (
		<div className="space-y-4" data-testid="m05-runtime-operations">
			<section className="rounded-md border border-surface-border bg-surface-card p-4">
				<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
					<div>
						<div className="flex items-center gap-2">
							<Settings2 className="h-4 w-4 text-brand-primary" aria-hidden="true" />
							<h3 className="text-base font-semibold text-text-primary">主 Agent 运行参数</h3>
						</div>
						<p className="mt-1 max-w-3xl text-sm leading-6 text-text-secondary">
							这里维护线上主链路的 SDK read loop、步数预算、Q-deep 副作用门和写动作模式。
						</p>
					</div>
					<div className="flex flex-wrap gap-2">
						<RuntimePill tone={sdkLoopEnabled ? "success" : "neutral"}>
							{sdkLoopEnabled ? "SDK loop 启用" : "SDK loop 关闭"}
						</RuntimePill>
						<RuntimePill tone={liveConfirmEnabled ? "warning" : "neutral"}>
							{runtimeWriteModeLabel(writeMode)}
						</RuntimePill>
					</div>
				</div>

				<div className="mt-4 grid gap-3 lg:grid-cols-4">
					<label className="grid gap-1 text-sm">
						<span className="text-xs text-text-muted">商户 ID</span>
						<input
							value={merchantId}
							onChange={(event) => setMerchantId(event.target.value)}
							className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
						/>
					</label>
					<span className="flex items-center justify-between gap-3 rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm">
						<span>
							<span className="block font-medium text-text-primary">SDK read loop</span>
							<span className="text-xs text-text-muted">只读工具自主补查</span>
						</span>
						<Switch checked={sdkLoopEnabled} onCheckedChange={setSdkLoopEnabled} />
					</span>
					<label className="grid gap-1 text-sm">
						<span className="text-xs text-text-muted">最大工具步数</span>
						<input
							type="number"
							min={1}
							max={12}
							value={maxSteps}
							onChange={(event) =>
								setMaxSteps(Math.max(1, Math.min(12, Number(event.target.value) || 1)))
							}
							className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
						/>
					</label>
					<div className="grid gap-1 text-sm">
						<span className="text-xs text-text-muted">质量门</span>
						<Select
							className="w-full"
							aria-label="质量门"
							value={qualityMode}
							onChange={(event) => setQualityMode(event.target.value as AgentQualityMode)}
						>
							<option value="q_deep_side_effect">Q-deep 副作用门</option>
							<option value="q_fast_grounding_side_effect">Q-fast + 副作用门</option>
						</Select>
					</div>
				</div>

				<div className="mt-3 grid gap-3 lg:grid-cols-[260px_minmax(0,1fr)_160px]">
					<div className="grid gap-1 text-sm">
						<span className="text-xs text-text-muted">写动作模式</span>
						<Select
							className="w-full"
							aria-label="写动作模式"
							value={writeMode}
							onChange={(event) => setWriteMode(event.target.value as AgentWriteMode)}
						>
							<option value="disabled">禁用写动作</option>
							<option value="dry_run">仅 dry-run</option>
							<option value="live_confirmed">Admin 确认后 live write</option>
						</Select>
					</div>
					<label className="grid gap-1 text-sm">
						<span className="text-xs text-text-muted">备注</span>
						<input
							value={runtimeNotes}
							onChange={(event) => setRuntimeNotes(event.target.value)}
							className="h-9 rounded-md border border-surface-border bg-surface-background px-3 text-sm"
							placeholder="记录本次切换原因"
						/>
					</label>
					<div className="flex items-end">
						<Button type="button" onClick={onSaveRuntime} data-testid="m05-save-runtime">
							<Save className="mr-2 h-4 w-4" />
							保存参数
						</Button>
					</div>
				</div>
				<div className="mt-2 text-sm text-text-secondary" data-testid="m05-runtime-status">
					{isRuntimeLoading ? "加载运行参数中..." : runtimeStatus}
				</div>
			</section>

			<section className="rounded-md border border-surface-border bg-surface-card p-4">
				<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
					<div>
						<div className="flex items-center gap-2">
							<ShieldCheck className="h-4 w-4 text-brand-primary" aria-hidden="true" />
							<h3 className="text-base font-semibold text-text-primary">写动作安全门</h3>
						</div>
						<p className="mt-1 max-w-3xl text-sm leading-6 text-text-secondary">
							SK07-SK10 在主 Agent 内只产出 plan-only dry-run；live write 必须从下方审计队列确认。
						</p>
					</div>
					<RuntimePill
						tone={runtimeConfig?.live_write_mutation_direct_from_agent ? "blocked" : "success"}
					>
						{runtimeConfig?.live_write_mutation_direct_from_agent
							? "主 Agent 可直接写入"
							: "主 Agent 不直写"}
					</RuntimePill>
				</div>
				<div className="mt-4 grid gap-3 md:grid-cols-3">
					<GuardrailCard title="确认链" value="required" detail="客户确认和运营确认都必须存在" />
					<GuardrailCard
						title="Q-deep"
						value={qualityMode === "q_deep_side_effect" ? "enabled" : "downgraded"}
						detail="live write 前检查 dry-run、blocked_operations 和幂等键"
					/>
					<GuardrailCard
						title="持久审计"
						value="write_action_audits"
						detail="pending、confirmed、rejected、live_result 全部留痕"
					/>
				</div>
			</section>

			<section className="rounded-md border border-surface-border bg-surface-card p-4">
				<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
					<div>
						<h3 className="text-base font-semibold text-text-primary">待确认写动作</h3>
						<p className="mt-1 text-sm leading-6 text-text-secondary">
							来自主 Agent dry-run 后持久化的 confirmation_plan；确认后才会调用 Shopify Admin
							GraphQL。
						</p>
					</div>
					<RuntimePill tone={writeActions.length > 0 ? "warning" : "neutral"}>
						{isWriteActionsLoading ? "加载中" : `${writeActions.length} 条待处理`}
					</RuntimePill>
				</div>
				<div className="mt-4 overflow-x-auto rounded-md border border-surface-border">
					<table className="w-full min-w-[980px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">审计 ID</th>
								<th className="px-3 py-2 font-medium">Skill / 工具</th>
								<th className="px-3 py-2 font-medium">订单</th>
								<th className="px-3 py-2 font-medium">幂等键</th>
								<th className="px-3 py-2 font-medium">状态</th>
								<th className="px-3 py-2 font-medium">操作</th>
							</tr>
						</thead>
						<tbody>
							{writeActions.length === 0 ? (
								<tr>
									<td colSpan={6} className="px-3 py-6 text-center text-text-secondary">
										暂无待确认写动作
									</td>
								</tr>
							) : (
								writeActions.map((item) => (
									<WriteActionRow
										key={item.audit_id}
										item={item}
										liveConfirmEnabled={liveConfirmEnabled}
										onConfirm={onConfirmWriteAction}
										onReject={onRejectWriteAction}
									/>
								))
							)}
						</tbody>
					</table>
				</div>
				<div className="mt-2 text-sm text-text-secondary" data-testid="m05-write-action-status">
					{writeActionStatus}
				</div>
			</section>
		</div>
	);
}

function GuardrailCard({ title, value, detail }: { title: string; value: string; detail: string }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-background p-3">
			<div className="text-xs font-medium text-text-muted">{title}</div>
			<div className="mt-2 font-mono text-xs font-semibold text-text-primary">{value}</div>
			<p className="mt-2 text-xs leading-5 text-text-secondary">{detail}</p>
		</div>
	);
}

function WriteActionRow({
	item,
	liveConfirmEnabled,
	onConfirm,
	onReject,
}: {
	item: WriteActionItem;
	liveConfirmEnabled: boolean;
	onConfirm: (item: WriteActionItem) => void;
	onReject: (item: WriteActionItem) => void;
}) {
	return (
		<tr className="border-t border-surface-border align-top">
			<td className="px-3 py-3 font-mono text-xs text-text-primary">{item.audit_id}</td>
			<td className="px-3 py-3">
				<div className="font-medium text-text-primary">
					{item.skill_id} / {item.tool_id}
				</div>
				<div className="mt-1 text-xs text-text-muted">{item.trace_id ?? "无 trace"}</div>
			</td>
			<td className="px-3 py-3 font-mono text-xs text-text-secondary">
				{shortRuntimeId(item.order_id)}
			</td>
			<td className="px-3 py-3 font-mono text-xs text-text-secondary">{item.idempotency_key}</td>
			<td className="px-3 py-3">
				<RuntimePill tone={item.status === "pending_confirmation" ? "warning" : "neutral"}>
					{writeActionStatusLabel(item.status)}
				</RuntimePill>
			</td>
			<td className="px-3 py-3">
				<div className="flex flex-wrap gap-2">
					<Button
						type="button"
						size="sm"
						disabled={!liveConfirmEnabled}
						onClick={() => onConfirm(item)}
					>
						<CheckCircle2 className="mr-2 h-4 w-4" />
						确认执行
					</Button>
					<Button type="button" variant="outline" size="sm" onClick={() => onReject(item)}>
						<XCircle className="mr-2 h-4 w-4" />
						拒绝
					</Button>
				</div>
			</td>
		</tr>
	);
}

function RuntimePill({
	children,
	tone,
}: {
	children: string;
	tone: "success" | "warning" | "blocked" | "neutral";
}) {
	const toneClass =
		tone === "success"
			? "border-brand-success/30 bg-brand-success/10 text-brand-success"
			: tone === "warning"
				? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
				: tone === "blocked"
					? "border-brand-danger/30 bg-brand-danger/10 text-brand-danger"
					: "border-surface-border bg-surface-subtle text-text-secondary";
	return (
		<span className={`inline-flex rounded-md border px-2 py-1 text-xs ${toneClass}`}>
			{children}
		</span>
	);
}

function PromptAssetTestMatrix() {
	return (
		<section
			className="scroll-mt-6 rounded-md border border-surface-border bg-surface-card p-4"
			data-testid="prompt-asset-test-matrix"
		>
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<h3 className="text-base font-semibold text-text-primary">资产生效与测试矩阵</h3>
					<p className="mt-1 max-w-3xl text-sm leading-6 text-text-secondary">
						把会影响回复效果的提示词、规则、方案、工具、知识和评测规则放在同一张表里，先看是否真实生效，再决定去哪里验证。
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<MatrixLink href="/prompt-q#prompt-sandbox">看本页验证</MatrixLink>
					<MatrixLink href="/eval#eval-run">去评测运行</MatrixLink>
				</div>
			</div>

			<div className="mt-4 overflow-x-auto">
				<table className="w-full min-w-[1080px] border-collapse text-sm">
					<thead className="bg-surface-subtle text-left text-xs text-text-muted">
						<tr>
							<th className="px-3 py-2 font-medium">资产</th>
							<th className="px-3 py-2 font-medium">生效状态</th>
							<th className="px-3 py-2 font-medium">影响范围</th>
							<th className="px-3 py-2 font-medium">事实证据</th>
							<th className="px-3 py-2 font-medium">测试路径</th>
							<th className="px-3 py-2 font-medium">入口</th>
						</tr>
					</thead>
					<tbody>
						{PROMPT_ASSET_TEST_ROWS.map((row) => (
							<tr key={row.asset} className="border-t border-surface-border align-top">
								<td className="px-3 py-3 font-medium text-text-primary">{row.asset}</td>
								<td className="px-3 py-3">
									<span
										className={`inline-flex rounded-md border px-2 py-1 text-xs font-medium ${assetEffectStatusClass(
											row.effect,
										)}`}
									>
										{row.effect}
									</span>
								</td>
								<td className="px-3 py-3 leading-6 text-text-secondary">{row.scope}</td>
								<td className="px-3 py-3 font-mono text-xs leading-5 text-text-secondary">
									{row.evidence}
								</td>
								<td className="px-3 py-3 leading-6 text-text-secondary">{row.testPath}</td>
								<td className="px-3 py-3">
									<div className="flex flex-col gap-1">
										<a
											href={row.configHref}
											className="text-sm font-medium text-brand-primary hover:underline"
										>
											配置页
										</a>
										<a
											href={row.testHref}
											className="text-sm font-medium text-brand-primary hover:underline"
										>
											验证页
										</a>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	);
}

function PromptSingleStageContract() {
	return (
		<section
			id="prompt-single-stage-contract"
			className="scroll-mt-6 rounded-md border border-brand-warning/30 bg-brand-warning/10 p-4"
			data-testid="prompt-single-stage-contract"
		>
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<h3 className="text-base font-semibold text-brand-warning">回复生成单环节测试</h3>
					<p className="mt-1 max-w-3xl text-sm leading-6 text-brand-warning">
						把“回复生成”从页面沙盒推进到可运行入口：复用运行生成事件，先看主 Agent
						是否正确消费证据、追问缺参、守住写操作边界和客服表达，不把它升级成完整自主工具循环。
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<MatrixLink href="/test-datasets?mock=1#p10-single-stage-prompt-contract">
						去样本合同
					</MatrixLink>
					<MatrixLink href="/eval?mock=1&source=p10_single_stage&sample_id=P10TA001&target_stage=prompt_response&candidate_mode=manual#single-stage-candidate">
						带入评测运营
					</MatrixLink>
				</div>
			</div>

			<div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
				{PROMPT_SINGLE_STAGE_FACT_ROWS.map((item) => (
					<div
						key={item.label}
						className="rounded-md border border-brand-warning/30 bg-white/80 p-3"
					>
						<div className="text-xs font-medium text-brand-warning">{item.label}</div>
						<div className="mt-2 font-mono text-xs font-semibold text-brand-warning">
							{item.value}
						</div>
						<p className="mt-2 text-xs leading-5 text-brand-warning">{item.detail}</p>
					</div>
				))}
			</div>

			<div className="mt-4 overflow-x-auto rounded-md border border-brand-warning/30 bg-white/80">
				<table className="w-full min-w-[880px] border-collapse text-sm">
					<thead className="bg-brand-warning/15 text-left text-xs text-brand-warning">
						<tr>
							<th className="px-3 py-2 font-medium">检查项</th>
							<th className="px-3 py-2 font-medium">验收口径</th>
							<th className="px-3 py-2 font-medium">代表样本</th>
						</tr>
					</thead>
					<tbody>
						{PROMPT_SINGLE_STAGE_CHECK_ROWS.map((row) => (
							<tr key={row.check} className="border-t border-brand-warning/30 align-top">
								<td className="px-3 py-3 font-medium text-brand-warning">{row.check}</td>
								<td className="px-3 py-3 leading-6 text-brand-warning">{row.expectation}</td>
								<td className="px-3 py-3 font-mono text-xs text-brand-warning">{row.sample}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="mt-4 rounded-md border border-brand-warning/30 bg-white/80 p-3 text-sm leading-6 text-brand-warning">
				<div className="font-semibold text-brand-warning">边界说明</div>
				<p className="mt-1">
					“运行回复生成”复用现有运行生成事件，不新增独立提示词接口，不改主回复链路，不把场景话术堆进主
					Agent；推荐、对比、缺参、确认类表达仍优先由方案、工具、知识和证据注入承接。
				</p>
			</div>
		</section>
	);
}

function MainAgentToolModeExplainer() {
	return (
		<section
			id="main-agent-tool-mode"
			className="scroll-mt-6 rounded-md border border-surface-border bg-surface-card p-4"
			data-testid="main-agent-tool-mode-explainer"
		>
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<h3 className="text-base font-semibold text-text-primary">主 Agent 工具调用边界</h3>
					<p className="mt-1 max-w-3xl text-sm leading-6 text-text-secondary">
						当前主链路保留预取式证据注入，同时 M0 已接入 SDK 工具循环的开关路径。
						只读补查受方案白名单、预算和去重约束；写操作计划、保护性试跑和真实写入仍要按样本、
						合同、审计和验收逐层推进。
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<MatrixLink href="/test-datasets">去样本合同</MatrixLink>
					<MatrixLink href="/pipeline-testbench">去链路测试台</MatrixLink>
				</div>
			</div>

			<div className="mt-4 grid gap-3 lg:grid-cols-5">
				{MAIN_AGENT_TOOL_MODE_ROWS.map((item) => (
					<div
						key={item.mode}
						className="rounded-md border border-surface-border bg-surface-background p-3"
					>
						<div className="flex items-center justify-between gap-2">
							<span className="font-mono text-xs font-semibold text-brand-primary">
								{item.mode}
							</span>
							<span
								className={`rounded-md border px-2 py-1 text-xs font-medium ${toolModeStatusClass(
									item.status,
								)}`}
							>
								{item.status}
							</span>
						</div>
						<h4 className="mt-3 text-sm font-semibold leading-5 text-text-primary">{item.title}</h4>
						<p className="mt-2 text-sm leading-6 text-text-secondary">{item.detail}</p>
						<p className="mt-3 font-mono text-xs leading-5 text-text-muted">{item.check}</p>
					</div>
				))}
			</div>

			<div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_300px]">
				<div className="rounded-md border border-surface-border bg-surface-subtle p-3">
					<div className="text-sm font-semibold text-text-primary">当前事实口径</div>
					<ul className="mt-2 space-y-2 text-sm leading-6 text-text-secondary">
						{MAIN_AGENT_TOOL_MODE_FACTS.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				</div>
				<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-sm leading-6 text-brand-warning">
					<div className="font-semibold">验收边界</div>
					<p className="mt-1">
						M0 默认启用只读工具 loop；写类工具只能先生成 dry-run 和 confirmation_plan，live write
						必须从 Admin 写动作确认队列进入。
					</p>
				</div>
			</div>
		</section>
	);
}

function PromptAssetChangeAudit() {
	return (
		<section
			className="scroll-mt-6 rounded-md border border-surface-border bg-surface-card p-4"
			data-testid="prompt-asset-change-audit"
		>
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<div className="flex items-center gap-2">
						<ClipboardCheck className="h-4 w-4 text-brand-primary" aria-hidden="true" />
						<h3 className="text-base font-semibold text-text-primary">资产变更审计最小链路</h3>
					</div>
					<p className="mt-1 max-w-3xl text-sm leading-6 text-text-secondary">
						把“改了什么、为什么改、怎么验证、结果如何”变成固定检查链路。当前先做页面口径和字段同步，后端持久化后续补齐。
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<MatrixLink href="/dictionary#field-family-asset_change_audit">去字段矩阵</MatrixLink>
					<MatrixLink href="/prompt-q#prompt-sandbox">看本页验证</MatrixLink>
					<MatrixLink href="/eval#eval-run">去评测运行</MatrixLink>
				</div>
			</div>

			<div className="mt-4 grid gap-3 lg:grid-cols-5">
				{ASSET_CHANGE_AUDIT_STEPS.map((item) => (
					<div
						key={item.step}
						className="rounded-md border border-surface-border bg-surface-background p-3"
					>
						<div className="flex items-center justify-between gap-2">
							<span className="font-mono text-xs font-semibold text-brand-primary">
								{item.step}
							</span>
							<span className="rounded-md bg-surface-subtle px-2 py-1 text-xs text-text-muted">
								{item.owner}
							</span>
						</div>
						<h4 className="mt-3 text-sm font-semibold leading-5 text-text-primary">{item.title}</h4>
						<p className="mt-2 text-sm leading-6 text-text-secondary">{item.detail}</p>
						<p className="mt-3 font-mono text-xs leading-5 text-text-muted">{item.evidence}</p>
					</div>
				))}
			</div>

			<div className="mt-4 grid gap-3 rounded-md border border-surface-border bg-surface-subtle p-3 lg:grid-cols-[minmax(0,1fr)_280px]">
				<div>
					<div className="text-sm font-semibold text-text-primary">最小审计字段</div>
					<div className="mt-2 flex flex-wrap gap-2">
						{ASSET_CHANGE_AUDIT_FIELDS.map((field) => (
							<span
								key={field}
								className="rounded-md border border-surface-border bg-surface-background px-2 py-1 font-mono text-xs text-text-secondary"
							>
								{field}
							</span>
						))}
					</div>
				</div>
				<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-sm leading-6 text-brand-warning">
					后端持久化后续补齐：本轮不新增数据库表，不自动归因，不改变提示词、方案、知识或工具的发布逻辑。
				</div>
			</div>
		</section>
	);
}

function MatrixLink({ href, children }: { href: string; children: string }) {
	return (
		<a
			href={href}
			className="inline-flex items-center rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
		>
			{children}
			<ArrowRight className="ml-2 h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
		</a>
	);
}

function assetEffectStatusClass(effect: string): string {
	if (effect === "真实生效")
		return "border-brand-success/30 bg-brand-success/10 text-brand-success";
	if (effect === "配置已落，运行链路待接入")
		return "border-brand-warning/30 bg-brand-warning/10 text-brand-warning";
	if (effect === "沙盒和主链路分离") return "border-brand-info/30 bg-brand-info/10 text-brand-info";
	if (effect === "条件生效") return "border-brand-info/30 bg-brand-info/10 text-brand-info";
	return "border-surface-border bg-surface-subtle text-text-secondary";
}

function toolModeStatusClass(status: string): string {
	if (status === "已落主路径")
		return "border-brand-success/30 bg-brand-success/10 text-brand-success";
	if (status === "P10TA010 已补") return "border-brand-info/30 bg-brand-info/10 text-brand-info";
	if (status === "已上线受控")
		return "border-brand-warning/30 bg-brand-warning/10 text-brand-warning";
	if (status === "未开启") return "border-surface-border bg-surface-subtle text-text-secondary";
	return "border-brand-warning/30 bg-brand-warning/10 text-brand-warning";
}

function runtimeWriteModeLabel(mode: AgentWriteMode): string {
	if (mode === "live_confirmed") return "Admin 确认后 live write";
	if (mode === "dry_run") return "仅 dry-run";
	return "禁用写动作";
}

function writeActionStatusLabel(status: string): string {
	if (status === "pending_confirmation") return "等待确认";
	if (status === "live_executed") return "已执行";
	if (status === "live_failed") return "执行失败";
	if (status === "rejected") return "已拒绝";
	if (status === "expired") return "已过期";
	return status;
}

function shortRuntimeId(value: string | null): string {
	if (!value) return "未返回";
	const parts = value.split("/");
	return parts[parts.length - 1] || value;
}

function VersionRow({ item }: { item: PromptVersionListItem }) {
	return (
		<tr className="border-t border-surface-border">
			<td className="px-3 py-2 font-mono">{item.version_id}</td>
			<td className="px-3 py-2">{promptVersionStatusLabel(item.status)}</td>
			<td className="px-3 py-2 font-mono">{item.region_count}</td>
			<td className="px-3 py-2 font-mono">{item.main_agent_token_estimate}</td>
			<td className="px-3 py-2 font-mono">{item.d_prime_token_estimate}</td>
			<td className="px-3 py-2 text-text-secondary">{item.updated_by ?? "system"}</td>
		</tr>
	);
}

function promptVersionStatusLabel(status: string): string {
	if (status === "published") return "已发布";
	if (status === "draft") return "草稿";
	return status;
}

function Metric({
	label,
	value,
	compact = false,
}: { label: string; value: number | string; compact?: boolean }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-card px-4 py-3">
			<div className="text-xs text-text-muted">{label}</div>
			<div className={`mt-1 font-mono font-semibold ${compact ? "text-lg" : "text-2xl"}`}>
				{value}
			</div>
		</div>
	);
}
